import { Badge, CardContent } from '@invent/wl-ui-kit-next/ui';

import { ParticipantAvatar } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type { IAdminParticipantDetail } from '../../api/participant-contracts';
import { absent } from '../../components/account-facts';
import { Fact, Section } from '../../components/admin-page';
import { CopyableValue } from '../../components/copyable-value';
import { UnverifiedBadge } from '../../components/unverified-badge';
import { MemberChecks } from './checks';
import { MemberPart } from './household';
import { MemberNumbers, type INumberWrites } from './numbers';

/**
 * Who a member's record is, at the head of its part of Identity & numbers — the kind, the id,
 * when it was made, whether any number is verified and whether its owner denied one. The name
 * is the page's title, or the member's heading in a household of more than one. Drawn from the
 * card as sent; the verification line is `unverified` said in words, never recomputed from the
 * numbers below it.
 */

function IdentityFacts({
  participant,
  multi,
  recordPage
}: {
  participant: IAdminParticipantDetail;
  /** A household of more than one: each member's copy button names whose id it copies. */
  multi: boolean;
  /** A record page (SR-407): the kind, the id, the day it was made and the photo are the record
   *  card's, so only the two facts the card does not say are drawn. */
  recordPage: boolean;
}) {
  return (
    <div className="grid gap-3 text-sm sm:grid-cols-2">
      {!recordPage && (
        <>
          <Fact label="Kind" value={<Badge variant="soft">{participant.kind}</Badge>} />
          <Fact
            label="Participant id"
            value={
              <CopyableValue
                value={participant.participantId}
                name={
                  multi
                    ? `Copy ${participant.displayName}’s participant id`
                    : 'Copy the participant id'
                }
              />
            }
          />
          <Fact label="Created" value={formatDateTime(participant.createdAt)} />
        </>
      )}
      <Fact
        label="Verification"
        value={
          participant.unverified ? (
            <>
              <UnverifiedBadge />
              <span className="mt-1 block text-muted-foreground">
                No number here is verified — the normal state for a migrated participant.
              </span>
            </>
          ) : (
            'At least one number is verified.'
          )
        }
      />
      <Fact
        label="Owner’s answers"
        value={
          participant.denied ? (
            <>
              <Badge variant="destructive">Denied a number</Badge>
              <span className="mt-1 block text-muted-foreground">
                Its owner said a number held here is not theirs — see its row below.
              </span>
            </>
          ) : (
            absent('No number denied.')
          )
        }
      />
      {!recordPage && (
        <Fact
          label="Photo"
          value={
            participant.photoUri ? (
              <ParticipantAvatar
                photoUri={participant.photoUri}
                displayName={participant.displayName}
                size="portrait"
              />
            ) : (
              absent('None held.')
            )
          }
        />
      )}
    </div>
  );
}

/**
 * Identity & numbers (R6, R8): each member's record, its numbers and its number checks (SR-487),
 * the opened participant first, each under its name where the household has more than one. What a
 * press on a number was told is the page's, keyed by the member as well as the number
 * (`useNumberNotes`), so two members' notes never mix and the header's press says each beside its
 * own number.
 */
export function IdentitySection({
  members,
  anchor,
  writes,
  recordPage = false
}: {
  members: IAdminParticipantDetail[];
  anchor: string;
  writes: INumberWrites;
  recordPage?: boolean;
}) {
  const multi = members.length > 1;
  // On a record page what is left here is the numbers and their state: with no number held and
  // none denied the part says only "none" three ways, so it folds to its count (SR-407).
  const empty =
    recordPage &&
    members.every(
      (member) =>
        member.identifiers.length === 0 &&
        (member.checks ?? []).length === 0 &&
        !member.denied &&
        member.pendingLookups.length === 0 &&
        // A note a write left about a number since gone is said here, under its own heading.
        !Object.keys(writes.said).some((key) => key.startsWith(`["${member.participantId}",`))
    );
  return (
    <Section title="Identity & numbers" anchor={anchor} empty={empty}>
      <CardContent className="grid gap-8">
        {members.map((member, index) => (
          <MemberPart key={member.participantId} member={member} opened={index === 0} multi={multi}>
            <IdentityFacts participant={member} multi={multi} recordPage={recordPage} />
            <MemberNumbers participant={member} headingLevel={multi ? 4 : 3} writes={writes} />
            <MemberChecks
              participantId={member.participantId}
              checks={member.checks}
              headingLevel={multi ? 4 : 3}
            />
          </MemberPart>
        ))}
      </CardContent>
    </Section>
  );
}
